// WARNING: THIS FILE IS SHARED TO THE CLIENT

function nestedCopy(object) {
  return JSON.parse(JSON.stringify(object));
}

export const APP_DOMAIN = "editor.sudowrite.com";
export const PLUGIN_CREATION_UNAVAILABLE_MESSAGE = "This isn't available on your free trial.";
export const PLUGIN_TESTING_UNAVAILABLE_MESSAGE = "This isn't available on your free trial.";

export const MAX_DOCUMENT_TITLE_LENGTH = 255;
export const MAX_ITEMS_PER_DOCUMENT = 1000;
export const MAX_MEMORY_CHARACTERS = 400;

export const EMAIL_PREFERENCE_KEYS = {
  CLASS_ANNOUNCEMENTS: "classAnnouncements",
  PRODUCT_UPDATES: "productUpdates",
  PRODUCT_TIPS_REMINDERS: "productTipsReminders",
  USER_FEEDBACK: "userFeedback",
};

export const EMAIL_PREFERENCE_DEFAULTS = {
  [EMAIL_PREFERENCE_KEYS.CLASS_ANNOUNCEMENTS]: true,
  [EMAIL_PREFERENCE_KEYS.PRODUCT_UPDATES]: true,
  [EMAIL_PREFERENCE_KEYS.PRODUCT_TIPS_REMINDERS]: true,
  [EMAIL_PREFERENCE_KEYS.USER_FEEDBACK]: true,
};

export const EMAIL_PREFERENCE_OPTIONS = [
  {
    key: EMAIL_PREFERENCE_KEYS.CLASS_ANNOUNCEMENTS,
    label: "Class Announcements",
    description: "Announcements for upcoming cohort classes, sign-up windows, and schedule reminders.",
    subscriptionId: 3,
  },
  {
    key: EMAIL_PREFERENCE_KEYS.PRODUCT_UPDATES,
    label: "Product Updates",
    description: "Major launches, roadmap updates, and behind-the-scenes changes to Sudowrite.",
    subscriptionId: 1,
  },
  {
    key: EMAIL_PREFERENCE_KEYS.PRODUCT_TIPS_REMINDERS,
    label: "Product Tips & Reminders",
    description: "Tips, prompts, and reminders that help writers get more value out of Sudowrite.",
    subscriptionId: 4,
  },
  {
    key: EMAIL_PREFERENCE_KEYS.USER_FEEDBACK,
    label: "User Feedback",
    description: "Invitations to surveys, research sessions, or beta testing opportunities.",
    subscriptionId: 5,
  },
];

export const MIN_REWRITE_WORDS = 3;
export const MAX_REWRITE_WORDS = 6000;
export const MIN_DESCRIBE_WORDS = 1;
export const MAX_DESCRIBE_WORDS = 500;
export const MIN_EXPAND_WORDS = 3;
export const MAX_EXPAND_WORDS = 1000;
export const MIN_VISUALIZE_WORDS = 10;
export const MAX_VISUALIZE_WORDS = 500;
export const MIN_DOC_TITLING_WORDS = 50;
export const MAX_QUICK_EDIT_PRECEDING_TEXT_WORDS = 1000;
export const MAX_QUICK_EDIT_HIGHLIGHTED_WORDS = 1000;
export const MIN_QUICK_EDIT_WORDS = 1;
export const MAX_CHARACTERS_IMPORT_INPUT_WORDS = 60_000;
export const MAX_WORLDBUILDING_IMPORT_INPUT_WORDS = 60_000;
export const MAX_OUTLINE_WORDS = 1700;
export const MAX_SYNOPSIS_WORDS = 4000;
export const MAX_BRAINDUMP_WORDS = 4000;
export const MAX_IMPORT_WIP_INPUT_WORDS = 130_000;
export const MAX_AGENTIC_IMPORT_INPUT_WORDS = 500_000;
export const MAX_SCRIVENER_ZIP_BYTES = 200 * 1024 * 1024;
export const MAX_TRIAL_NOVEL_IMPORTS = 2;
// center column widths:
export const MIN_WIDTH_WHEN_SHOWING_COMMENTS = 780;
export const MIN_WIDTH_WHEN_HIDING_COMMENTS = 520;

// Paragraph spacing options relative to line-height. 1x means paragraph gaps
// match line gaps (no extra margin). 1.5x / 2x add 0.5× / 1× line-height of
// extra margin-bottom (see getEditorParagraphSpacing). New accounts default
// to X1_5; legacy 0/0.75/2.5 font-size multipliers normalize onto this set.
export const PARAGRAPH_SPACING = {
  X1: 1,
  X1_5: 1.5,
  X2: 2,
};

// Product default for newly initialized accounts and explicit "Reset to
// default" actions. The client model deliberately keeps its separate X1
// fallback for legacy user-data documents where this field is missing.
export const DEFAULT_PARAGRAPH_SPACING = PARAGRAPH_SPACING.X1_5;

export const PARAGRAPH_SPACING_OPTIONS = [PARAGRAPH_SPACING.X1, PARAGRAPH_SPACING.X1_5, PARAGRAPH_SPACING.X2];

/** Snap stored/legacy paragraph-spacing values onto the current 1x/1.5x/2x set. */
export function normalizeParagraphSpacing(value) {
  if (value === PARAGRAPH_SPACING.X1 || value === PARAGRAPH_SPACING.X1_5 || value === PARAGRAPH_SPACING.X2) {
    return value;
  }
  // Pre-multiplier era: font-size multipliers 0 / 0.75 / 1.5 / 2.5.
  if (value === 0 || value === 0.75) return PARAGRAPH_SPACING.X1;
  if (value === 2.5) return PARAGRAPH_SPACING.X2;
  return PARAGRAPH_SPACING.X1;
}

export const AFFILIATES = {
  "paul-jenkins": {
    message: "You've been invited by Paul Jenkins to try Sudowrite 🎉",
  },
  thecreativepenn: {
    message: "You've been invited by The Creative Penn to try Sudowrite 🎉",
  },
  author_media: {
    message: "You've been invited by Author Media to try Sudowrite 🎉",
    description: "Sign up now to get an extra 4000 AI words during your trial",
    trialQuota: 8000,
    trialQuotaCredits: 8000 * 8.5,
  },
  "20-books-vegas": {
    trialQuota: 4000 + 15000,
    trialQuotaCredits: (4000 + 15000) * 8.5,
    // message: "Thanks for visiting Sudowrite at 20 Books Vegas!",
    description:
      "Sign up now to get an extra 15,000 AI words during your trial.<br/><br/> Already have an account? <a href='mailto:michael@sudowrite.com'>Email us</a> and we'll add those words for you.",
  },
};

// List of pre-canned filters
export const FILTER_MODIFIERS = [
  // { value: "be more interesting", label: "Rephrase" },
  // { value: "have a different vibe", label: "Rephrase" },
  { value: "rephrase", label: "Rephrase" },
  { value: "be more succinct", label: "Shorter" },
  { value: "be more descriptive", label: "More descriptive" },
  { value: "show, not tell", label: "Show, not tell" },
  { value: "be more conflicted", label: "More inner conflict" },
  // { value: "be more ominous", label: "More ominous" },
  // { value: "be more optimistic", label: "More optimistic" },
  // { value: "be more pessimistic", label: "More pessimistic" },
  // { value: "be more romantic", label: "More romantic" },
  // { value: "be more hilarious", label: "More hilarious" },
  { value: "be more intense", label: "More intense" },
  // { value: "be more erotic", label: "More erotic" },
  // { value: "be more absurd", label: "More absurd" },
  // { value: "be more surprising", label: "More surprising"},
  // { value: "contain a metaphor", label: "Add a metaphor" },
  { value: "custom", label: "Customize..." },
];

// Tones
export const TONES = [
  //{ label: 'Ominous', value: 'ominous', modifier: 'Continue the story, the long continuation should be ominous and foreboding' },
  {
    label: "Ominous",
    value: "ominous",
    modifier: "Continue the STORY in an ominous and foreboding direction",
  },
  //{ label: 'Fantastical', value: 'fantastical', modifier: 'Continue the story in a magical voice' },
  {
    label: "Fantastical",
    value: "fantastical",
    modifier: "Continue the STORY so that it becomes full of magical occurrences",
  },
  //{ label: 'Surreal', value: 'surreal', modifier: 'Continue the story, the long continuation should be surreal like Haruki Murakami' },
  {
    label: "Fast-Paced",
    value: "fast-paced",
    modifier: "Continue the STORY so that it becomes action-packed",
  },
  //{ label: 'Descriptive', value: 'descriptive', modifier: 'Continue the story in a new passage full of vivid description' },
  //{ label: 'Descriptive', value: 'descriptive', modifier: 'Continue the story by writing a scene which describes everything in meticulous detail' },
  {
    label: "Upbeat",
    value: "upbeat",
    modifier: "Continue the STORY in an optimistic and upbeat direction",
  },
  {
    label: "Authoritative",
    value: "authoritative",
    modifier: "Continue the STORY in the authoritative voice of an expert",
  },
  {
    label: "Conflicted",
    value: "conflicted",
    modifier: "Continue the STORY with a long passage in a deeply conflicted tone",
  },
  //{ label: 'Funny', value: 'funny', modifier: 'Continue the story in a funny tone like a Douglas Adams comic novel' },
  {
    label: "Romantic",
    value: "romantic",
    modifier: "Continue the STORY so that it becomes full of touching romance",
  },
  {
    label: "Sensual",
    value: "sensual",
    modifier: "Continue the STORY in a sexy and erotic direction",
  },
];

export const DEFAULT_TONE = TONES.find((tone) => tone.value === "ominous");

export const WORDS_PER_IMAGE = 200;

export const DESCRIBE_SENSES = [
  {
    label: "Sight",
    value: "sight",
    image: "/assets/noun_eyes_MODIFIED_2743436.svg",
  },
  {
    label: "Smell",
    value: "smell",
    image: "/assets/noun_smell_20407.svg",
  },
  {
    label: "Taste",
    value: "taste",
    image: "/assets/noun_taste_2173029.svg",
  },
  {
    label: "Sound",
    value: "sound",
    image: "/assets/noun_Listen_21603.svg",
  },
  {
    label: "Touch",
    value: "touch",
    image: "/assets/noun_touch_MODIFIED_181230.svg",
  },
  {
    label: "Metaphor",
    value: "metaphor",
    image: "/assets/noun_Rose_1282645.svg",
  },
];

// Write Modes
export const WRITE_MODES = [
  { label: "Auto", value: "auto" },
  { label: "Guided", value: "guided" },
  { label: "Tone", value: "tone" },
];

export const WRITE_MODE_AUTO = WRITE_MODES.find((mode) => mode.value === "auto");
export const WRITE_MODE_GUIDED = WRITE_MODES.find((mode) => mode.value === "guided");
export const WRITE_MODE_TONE = WRITE_MODES.find((mode) => mode.value === "tone");
export const DEFAULT_WRITE_MODE = WRITE_MODE_AUTO;

// Specifies the target output lengths for wormhole
export const WORMHOLE_OUTPUT_LENGTHS = [
  { label: "~50 words", value: 0, maxTokens: 66, context: 0.33 },
  { label: "~100 words", value: 1, maxTokens: 133, context: 0.66 },
  { label: "~150 words", value: 2, maxTokens: 200, context: 1 },
  { label: "~200 words", value: 3, maxTokens: 267, context: 1 },
  { label: "~250 words", value: 4, maxTokens: 333, context: 1 },
  { label: "~500 words", value: 5, maxTokens: 666, context: 1 },
  { label: "~1000 words", value: 7, maxTokens: 1330, context: 1 },
  { label: "~1500 words", value: 8, maxTokens: 1995, context: 1 },
  { label: "Max words", value: 6, maxTokens: 2666, context: 1 },
];

export const WRITE_MODELS = [
  { label: "Muse 2 Alpha", value: "muse-2-alpha", group: "Standard", guidanceCharacterLimit: 2500 },
  // Phase 1 standalone turtle candidates (Leonardo, Michelangelo, Donatello,
  // Raphael) were removed when Phase 2 launched. Saved values still fall back
  // via proseMode; ledger labels are kept for provenance.
  { label: "Basic", value: "basic", group: "Standard", guidanceCharacterLimit: 2500 },
  { label: "Excellent", value: "excellent", group: "Standard", guidanceCharacterLimit: 2500 },
  { label: "Unfiltered", value: "unfiltered", group: "Standard", guidanceCharacterLimit: 2500 },

  { label: "Best Prose (v1)", value: "bestProse", group: "Legacy", guidanceCharacterLimit: 2500 },
  { label: "Fastest", value: "fastest", group: "Legacy", guidanceCharacterLimit: 2500 },

  { label: "GPT 4o", value: "gpt4o", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "GPT 4o Mini", value: "gpt4omini", group: "Experimental", guidanceCharacterLimit: 2500 },

  { label: "GPT 4.1", value: "gpt41", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "GPT 4.1 Mini", value: "gpt41mini", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "GPT 5", value: "gpt5", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "GPT 5 Mini", value: "gpt5mini", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "GPT 5 Nano", value: "gpt5nano", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "GPT 5.4", value: "gpt54", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "GPT 5.5", value: "gpt55", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "GPT 5.6 Sol", value: "gpt56sol", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "GPT-6 Astra", value: "gpt6astra", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "GPT-6.1 Sol", value: "gpt61sol", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "GPT-6 Sol", value: "gpt6sol", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "GPT-6 Luna", value: "gpt6luna", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "GPT 5.6 Terra", value: "gpt56terra", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "GPT 5.6 Luna", value: "gpt56luna", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "Grok 4.3", value: "grok43", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "Grok 4.7", value: "grok47", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "GLM 5.3", value: "glm53", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "GLM 5.3 Flash", value: "glm53Flash", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "Qwen 3.8 Max", value: "qwen38Max", group: "Experimental", guidanceCharacterLimit: 2500 },

  { label: "Muse", value: "muse", group: "Standard", guidanceCharacterLimit: 2500 },
  { label: "Muse 1", value: "muse1", group: "Standard", guidanceCharacterLimit: 2500 },
  { label: "My Voice", value: "myVoice", group: "Standard", guidanceCharacterLimit: 2500 },

  { label: "Airoboros 70B", value: "airoboros70b", group: "Experimental", guidanceCharacterLimit: 500 }, // 4000
  { label: "Mythomax 13B", value: "mythomax", group: "Experimental", guidanceCharacterLimit: 500 }, // 4096
  { label: "Mistral Medium", value: "mistralMedium", group: "Experimental", guidanceCharacterLimit: 2500 }, // 32k
  { label: "DeepSeek R1", value: "deepSeekR1", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "DeepSeek V3", value: "deepSeekV3", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "DeepSeek V3.1", value: "deepSeekV31", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "DeepSeek V4 Pro", value: "deepSeekV4Pro", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "Gemini Pro 2.5", value: "gemini", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "Gemini 2.5 Flash Thinking", value: "geminiFlashThinking", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "Gemini 3 Flash", value: "gemini3Flash", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "Gemini 3.7 Flash", value: "gemini37Flash", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "Gemini 3.8 Flash", value: "gemini38Flash", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "Gemini 3.1 Pro", value: "gemini31Pro", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "Kimi K2", value: "kimiK2", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "GLM 5.2", value: "glm52", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "Kimi K3", value: "kimiK3", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "Kimi K2 Thinking", value: "kimiK2Thinking", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "Claude 3 Sonnet", value: "claude3sonnet", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "Claude 3.5 Sonnet", value: "claude35sonnet", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "Claude 4.5 Sonnet", value: "claude45sonnet", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "Claude 4.6 Sonnet", value: "claude46sonnet", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "Claude 5 Sonnet", value: "claude5sonnet", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "Claude 5.5 Sonnet", value: "claude55sonnet", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "Claude 5.5 Haiku", value: "claude55haiku", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "Claude 4.5 Haiku", value: "claude45haiku", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "Claude 4.1 Opus", value: "claude41opus", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "Claude 4.5 Opus", value: "claude45opus", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "Claude 4.6 Opus", value: "claude46opus", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "Claude 4.7 Opus", value: "claude47opus", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "Claude 4.8 Opus", value: "claude48opus", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "Claude 5 Opus", value: "claude5opus", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "Claude 5.5 Opus", value: "claude55opus", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "Claude 5 Fable", value: "claude5fable", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "Claude 5.1 Fable", value: "claude51fable", group: "Experimental", guidanceCharacterLimit: 2500 },
  { label: "o3", value: "o3", group: "Experimental", guidanceCharacterLimit: 2500 },
];

// Specifies how many cards to return for wormhole
export const WORMHOLE_OUTPUT_CARDS = [
  { label: "1", value: 1, context: 0.5 },
  { label: "2", value: 2, context: 1 },
  { label: "3", value: 3, context: 1 },
  { label: "4", value: 4, context: 1 },
  { label: "5", value: 5, context: 1 },
  { label: "6", value: 6, context: 1 },
];

export const REWRITE_OUTPUT_CARDS = [
  { label: "1", value: 1 },
  { label: "2", value: 2 },
  { label: "3", value: 3 },
  { label: "4", value: 4 },
  { label: "5", value: 5 },
  { label: "6", value: 6 },
];

export const PRIMARY_GENRES = [
  { label: "Romance", value: "romance" },
  { label: "Historical Fiction", value: "historical-fiction", guess: ["historical", "history", "world war", "ww2", "ww1", "war"] },
  { label: "Fantasy", value: "fantasy" },
  { label: "Science Fiction", value: "science-fiction", guess: ["sci-fi", "sci fi"] },
  { label: "Thriller", value: "thriller" },
  { label: "Horror", value: "horror" },
  { label: "Mystery", value: "mystery" },
  { label: "Young Adult", value: "young-adult", guess: [" ya ", "adolescent", "coming-of-age", "teen", "youth"] },
  { label: "Adventure", value: "adventure" },
  { label: "Crime", value: "crime", guess: ["detective"] },
  { label: "Literary Fiction", value: "literary-fiction", guess: ["literary"] },
  { label: "Non-Fiction", value: "non-fiction" },
  { label: "Other", value: "other" },
];

// list of additional genres we use for genre guessing
export const ADDITIONAL_GENRES = [{ label: "Erotica", value: "erotica", guess: ["erotic"] }];
// Max length of each item in brainstorm list, truncated on both cliet and server
export const MAX_BRAINSTORM_ITEM_LENGTH = 240;

export const COMMAND_MENU_MODES = [
  { label: "Normal", value: "normal", engine: "gpt-3.5-turbo" }, // fastest?
  { label: "High Quality", value: "highQuality", engine: "gpt-5.6-sol" }, // most accurate?
];

export const COMMAND_MENU_NORMAL = COMMAND_MENU_MODES.find((mode) => mode.value === "normal");
export const COMMAND_MENU_HIGH_QUALITY = COMMAND_MENU_MODES.find((mode) => mode.value === "highQuality");
export const DEFAULT_COMMAND_MENU_MODE = COMMAND_MENU_NORMAL;

// Agentic Chat Models
export const AGENTIC_CHAT_MODELS = [
  { label: "Model B", value: "glm-4.6", modelName: "GLM 4.6" },
  { label: "GLM 5.2", value: "glm-5.2", modelName: "GLM 5.2" },
  { label: "Model C", value: "kimi-k2-thinking", modelName: "Kimi K2 Thinking" },
  { label: "Model E", value: "kimi-k2.5", modelName: "Kimi K2.5" },
];

export const DEFAULT_AGENTIC_CHAT_MODEL =
  AGENTIC_CHAT_MODELS.find((model) => model.value === "kimi-k2.5") ??
  AGENTIC_CHAT_MODELS.find((model) => model.value === "glm-5.2") ??
  AGENTIC_CHAT_MODELS.find((model) => model.value === "kimi-k2-thinking") ??
  AGENTIC_CHAT_MODELS[0];

// Agentic Chat Modes (Write vs Chat)
export const AGENTIC_CHAT_MODES = [
  { label: "Chat only", value: "chat", modelKey: "kimi-k2.5", description: "For conversations and questions" },
  { label: "Allow edits", value: "write", modelKey: "kimi-k2.5", description: "For writing and editing" },
];

export const DEFAULT_AGENTIC_CHAT_MODE = AGENTIC_CHAT_MODES[0];

/**
 * Admin-only agentic brain picker options (GrowthBook experiment arms).
 * When an admin sends one of these as `body.model`, experiment middleware must not overwrite it
 * (requires subscription feature gate `adminAgenticChatBrainPicker`).
 */
export const ADMIN_AGENTIC_CHAT_BRAIN_MODELS = [
  { label: "Kimi K2.5", value: "kimi-k2.5" },
  { label: "Kimi K3", value: "kimi-k3" },
  { label: "Kimi K2.6", value: "kimi-k2.6" },
  { label: "GLM 5.2", value: "glm-5.2" },
];

export const DEFAULT_ADMIN_AGENTIC_CHAT_BRAIN_MODEL =
  ADMIN_AGENTIC_CHAT_BRAIN_MODELS.find((model) => model.value === "kimi-k2.5") ??
  ADMIN_AGENTIC_CHAT_BRAIN_MODELS[0];

export const ADMIN_AGENTIC_CHAT_BRAIN_MODEL_KEYS = new Set(
  ADMIN_AGENTIC_CHAT_BRAIN_MODELS.map((model) => model.value),
);

export function isAdminAgenticChatBrainModel(modelKey) {
  return typeof modelKey === "string" && ADMIN_AGENTIC_CHAT_BRAIN_MODEL_KEYS.has(modelKey);
}

/** GrowthBook feature id for SW-9080 import speed perception A/B test (control = legacy home wait flow). */
export const IMPORT_SPEED_PERCEPTION_EXPERIMENT_KEY = "import-speed";

/** Key in user_metadata.experiments; set once while on trial, read after subscribe. */
export const IMPORT_SPEED_PERCEPTION_COHORT_KEY = "import_speed";

export const IMPORT_SPEED_PERCEPTION_COHORT = {
  TREATMENT: "treatment",
  CONTROL: "control",
};

export function isImportSpeedPerceptionTreatmentCohort(cohort) {
  return cohort === IMPORT_SPEED_PERCEPTION_COHORT.TREATMENT;
}

/** GrowthBook feature id for trial default agentic chat composer mode A/B test. */
export const TRIAL_DEFAULT_AGENTIC_CHAT_MODE_EXPERIMENT_KEY = "trial-default-agentic-chat-mode";

/** Key in user_metadata.experiments; set once while on trial, read after subscribe. */
export const TRIAL_DEFAULT_AGENTIC_CHAT_MODE_COHORT_KEY = "trial_default_agentic_chat_mode";

export const TRIAL_DEFAULT_AGENTIC_CHAT_MODE_VALUES = {
  CHAT: "chat",
  WRITE: "write",
};

export function isTrialDefaultAgenticChatModeCohort(value) {
  return (
    value === TRIAL_DEFAULT_AGENTIC_CHAT_MODE_VALUES.CHAT ||
    value === TRIAL_DEFAULT_AGENTIC_CHAT_MODE_VALUES.WRITE
  );
}

/**
 * Historical experiment id retained for chat-onboarding analytics.
 * The chat treatment has graduated; new users are no longer randomized.
 */
export const CHAT_ONBOARDING_EXPERIMENT_KEY = "chat-onboarding";

/**
 * Key in user_metadata.experiments. Set when the chat-onboarding opener is created;
 * stores `{ cohort, projectId, chatId }` so the flow is idempotent and so the client
 * can suppress the sticky-tour onboarding for treatment users.
 */
export const CHAT_ONBOARDING_COHORT_KEY = "chat_onboarding";

/**
 * Historical project-home assignment retained to preserve legacy onboarding.
 * The new project UI is available to everyone; signup no longer enrolls users.
 */
export const LEGACY_NEW_PROJECT_FLOW_EXPERIMENT_KEY = "new-project-flow";

export const NEW_PROJECT_FLOW_VARIATIONS = {
  CONTROL: "control",
  TREATMENT: "new-project-flow",
};

/** Raindrop event name for the generated opener (follow-up turns share convoId = chatId). */
export const CHAT_ONBOARDING_RAINDROP_EVENT = "chat_onboarding_opener";

/** Raindrop event name for @sudowrite inline comment-thread agent replies. */
export const COMMENT_THREAD_RAINDROP_EVENT = "comment_thread";

export const IMPORT_WIP_STEPS = {
  CREATING: "Creating project and populating content...",
  TRAINING_MY_VOICE: "Training My Voice...",
  INITIAL_ANALYSIS: "Analyzing genre and story...",
  OUTLINE: "Extracting outline...",
  STORY_BIBLE_ENRICHMENT: "Extracting synopsis, characters, and worldbuilding...",
  CANCELLED: "Canceling import...",
  // Scrivener import steps
  EXTRACTING: "Extracting Scrivener backup...",
  PARSING: "Parsing project structure...",
  IMPORTING_DOCUMENTS: "Importing documents...",
};

// message used for imports that overflow the context window for the LLM
export const CONTEXT_OVERFLOW_MESSAGE = "Max tokens exceeded";

/******************/
/** PRICING PLANS */
/******************/

let PLANS_1_0;
let PLANS_2_0;
let PLANS_3_0;
let PLANS_4_0; // switches pricing from words to credits
let PACKS;
let CREDIT_PACKS;

// Approximately ratio used for converting words to credits
export const CREDITS_PER_WORD = 8.5;

/** Types of plan transitions */
export const PLAN_TRANSITION_TYPE = Object.freeze({
  PRORATED_REFUND_AND_TRANSITION_IMMEDIATELY: 0,
  CANCEL_AT_PERIOD_END_AND_SCHEDULE_TRANSITION: 1,
  TRANSITION_NATURALLY: 2, // a natural transition = change plan but don't prorate or reset billing cycle. member gets new allotment at end of billing cycle
});

if (process.env.NODE_ENV === "production") {
  // PRODUCTION

  PACKS = {
    // 2.0 $99 Plan
    legacy: [
      {
        name: "Small",
        quota: 25000,
        id: "price_1LHJSpFmAW2gX81XEnMxwRS9",
        amount: 1000,
      },
      {
        name: "Medium",
        quota: 62500,
        id: "price_1LHJSWFmAW2gX81XQDJex7Cu",
        amount: 2500,
      },
      {
        name: "Large",
        quota: 125000,
        id: "price_1LHJRjFmAW2gX81XMzQeAdbA",
        amount: 5000,
      },
      {
        name: "XL",
        quota: 250000,
        id: "price_1LHJS6FmAW2gX81XoGQ3tHQu",
        amount: 10000,
      },
    ],
    tier1: [
      {
        name: "Small",
        quota: 5000,
        id: "price_1LqTqhFmAW2gX81X7Qnj06OA",
        amount: 500,
      },
      {
        name: "Medium",
        quota: 10000,
        id: "price_1LqjDhFmAW2gX81X6bp7Oa2t",
        amount: 1000,
      },
      {
        name: "Large",
        quota: 25000,
        id: "price_1LHJSpFmAW2gX81XEnMxwRS9",
        amount: 2000,
      },
    ],
    tier2: [
      {
        name: "Small",
        id: "price_1LqjDhFmAW2gX81X6bp7Oa2t",
        quota: 10000,
        amount: 1000,
      },
      {
        name: "Medium",
        id: "price_1LqTqlFmAW2gX81XrFFUwWAn",
        quota: 30000,
        amount: 2500,
      },
      {
        name: "Large",
        id: "price_1LqTqoFmAW2gX81XsGcfpZ0W",
        quota: 75000,
        amount: 5000,
      },
      {
        name: "XL",
        id: "price_1LqTqrFmAW2gX81XnUMUb7t0",
        quota: 150000,
        amount: 9000,
      },
    ],
    tier3: [
      {
        name: "Small",
        id: "price_1LqTqjFmAW2gX81XJzhZXrYf",
        quota: 15000,
        amount: 1000,
      },
      {
        name: "Medium",
        id: "price_1LqTqmFmAW2gX81X4bHjJjsk",
        quota: 40000,
        amount: 2500,
      },
      {
        name: "Large",
        id: "price_1LqTqpFmAW2gX81X9e3X4n1D",
        quota: 100000,
        amount: 5000,
      },
      {
        name: "XL",
        id: "price_1LqTqsFmAW2gX81Xo3C7TOGE",
        quota: 250000,
        amount: 10000,
      },
    ],
  };

  CREDIT_PACKS = {
    tier1: [
      {
        name: "Small",
        quota: 100_000,
        id: "price_1OJfArFmAW2gX81XpQTXMgiI",
        amount: 500,
      },
      {
        name: "Medium",
        quota: 500_000,
        id: "price_1OJfBcFmAW2gX81XydJj1pO6",
        amount: 1900,
      },
      {
        name: "Large",
        quota: 1_750_000,
        id: "price_1OJfCZFmAW2gX81XAUAKbfby",
        amount: 4900,
      },
    ],
    tier2: [
      {
        name: "Small",
        id: "price_1OJfDbFmAW2gX81XjocN457O",
        quota: 200_000,
        amount: 900,
      },
      {
        name: "Medium",
        id: "price_1OJfEFFmAW2gX81XanGJA3hH",
        quota: 600_000,
        amount: 1900,
      },
      {
        name: "Large",
        id: "price_1OJfEmFmAW2gX81XGKykwYYc",
        quota: 1_900_000,
        amount: 4900,
      },
      {
        name: "XL",
        id: "price_1OJfFDFmAW2gX81XBaa5suOu",
        quota: 4_000_000,
        amount: 9900,
      },
    ],
    tier3: [
      {
        name: "Small",
        id: "price_1OJfDbFmAW2gX81XjocN457O",
        quota: 200_000,
        amount: 900,
      },
      {
        name: "Medium",
        id: "price_1OJfFjFmAW2gX81XwXVyilDI",
        quota: 1_000_000,
        amount: 2900,
      },
      {
        name: "Large",
        id: "price_1OJfGgFmAW2gX81XMmiFO7M7",
        quota: 2_000_000,
        amount: 4900,
      },
      {
        name: "XL",
        id: "price_1OJfHNFmAW2gX81XZ5bOFOHt",
        quota: 4_700_000,
        amount: 9900,
      },
    ],
  };

  PLANS_1_0 = [
    {
      id: "price_1HyVu3FmAW2gX81X9Fk7U5rk",
      price: 500,
      monthlyPrice: 500,
      title: "The Sudowrite Plan",
      allowOverage: false,
      allowPurchaseQuota: false,
      graceAmount: 0,
      allotment: 200000,
      hidden: true, // whether to display in the offerings
      interval: "month",
      hideAllotment: true, // whether to hide the allotment
    },
    {
      id: "price_1IIfJrFmAW2gX81X0fGSPWCF",
      price: 1000,
      monthlyPrice: 1000,
      title: "The Sudowrite Plan",
      allowOverage: false,
      allowPurchaseQuota: false,
      graceAmount: 0,
      allotment: 200000,
      hidden: true, // whether to display in the offerings
      interval: "month",
      hideAllotment: true, // whether to hide the allotment
    },
    {
      id: "price_1IbvI2FmAW2gX81XJj906aU3",
      price: 1500,
      monthlyPrice: 1500,
      title: "The Sudowrite Plan",
      allowOverage: false,
      allowPurchaseQuota: false,
      graceAmount: 0,
      allotment: 200000,
      hidden: true, // whether to display in the offerings
      interval: "month",
      hideAllotment: true, // whether to hide the allotment
    },
    {
      id: "price_1IbvHxFmAW2gX81XKHfHdKla",
      price: 2000,
      monthlyPrice: 2000,
      title: "The Sudowrite Plan",
      description: "Our best-in-class AI writing tools",
      allowOverage: false,
      allowPurchaseQuota: false,
      graceAmount: 0,
      allotment: 200000,
      interval: "month",
      hideAllotment: true, // whether to hide the allotment
    },
    {
      id: "price_1ITCEHFmAW2gX81X8jAoGkuz",
      price: 2500,
      monthlyPrice: 2500,
      title: "The Sudowrite Plan",
      allowOverage: false,
      allowPurchaseQuota: false,
      graceAmount: 0,
      allotment: 200000,
      hidden: true, // whether to display in the offerings
      interval: "month",
      hideAllotment: true, // whether to hide the allotment
    },
    {
      id: "price_1KAHcEFmAW2gX81XZNsMb28m", // enterprise
      price: 3333,
      monthlyPrice: 3333,
      title: "The Sudowrite Plan",
      allowOverage: false,
      allowPurchaseQuota: false,
      graceAmount: 0,
      allotment: 200000,
      hidden: true, // whether to display in the offerings
      interval: "month",
      hideAllotment: true, // whether to hide the allotment
    },
    {
      id: "price_1K1PcWFmAW2gX81X70EslH4f", // enterprise
      price: 3333,
      monthlyPrice: 3333,
      title: "The Sudowrite Plan",
      allowOverage: false,
      allowPurchaseQuota: false,
      graceAmount: 0,
      allotment: 200000,
      hidden: true, // whether to display in the offerings
      interval: "month",
      hideAllotment: true, // whether to hide the allotment
    },
    {
      id: "price_1K1PftFmAW2gX81XyGRnVE02", // enterprise free seat
      price: 1000,
      monthlyPrice: 1000,
      title: "The Sudowrite Plan",
      allowOverage: false,
      allowPurchaseQuota: false,
      graceAmount: 0,
      allotment: 200000,
      hidden: true, // whether to display in the offerings
      interval: "month",
      hideAllotment: true, // whether to hide the allotment
    },
    {
      id: "price_1HzUh3FmAW2gX81XuGzelOTy", // friends of sudowrite
      price: 0,
      monthlyPrice: 0,
      title: "The Sudowrite Plan",
      allowOverage: false,
      allowPurchaseQuota: false,
      graceAmount: 0,
      allotment: 200000,
      hidden: true, // whether to display in the offerings
      interval: "month",
      hideAllotment: true, // whether to hide the allotment
    },
  ];

  PLANS_2_0 = [
    {
      id: "price_1KZcKeFmAW2gX81XYLBbJi8o",
      title: "Limited Free",
      description: "Good for short stories, blog posts, or essays",
      allotment: 1000,
      price: 0,
      monthlyPrice: 0,
      allowOverage: false,
      allowPurchaseQuota: true,
      graceAmount: 100,
      defaultWormholeOutputCards: 2,
      interval: "month",
    },
    {
      id: "price_1KfqYzFmAW2gX81XT62RUjuE",
      price: 999,
      monthlyPrice: 999,
      title: "Hobby & Student",
      allotment: 10000,
      description: "Good for short stories, blog posts, or essays",
      allowOverage: false,
      allowPurchaseQuota: true,
      graceAmount: 1000,
      defaultWormholeOutputCards: 2,
      interval: "month",
      creditTargetPlanId: "price_1OIguAFmAW2gX81X2WcuXZan",
      couponTargetPlan: "a0SCUA7x",
    },
    {
      id: "price_1KZcKeFmAW2gX81X2KXfnnWh",
      price: 2900,
      monthlyPrice: 2900,
      title: "Professional",
      allotment: 50000,
      description: "Good for longer works, like a novel or screenplay",
      allowOverage: false,
      allowPurchaseQuota: true,
      graceAmount: 1000,
      defaultWormholeOutputCards: 3,
      interval: "month",
      creditTargetPlanId: "price_1OIi9JFmAW2gX81Xx9UIdO4d",
    },
    {
      id: "price_1KZcKeFmAW2gX81XitBIDpMZ",
      title: "Professional+",
      allotment: 200000,
      description: "For those looking to work AI deeply into their process",
      price: 9900,
      monthlyPrice: 9900,
      graceAmount: 0,
      allowOverage: false,
      allowPurchaseQuota: true,
      packSet: "legacy",
      defaultWormholeOutputCards: 4,
      interval: "month",
      creditTargetPlanId: "price_1OIiBxFmAW2gX81Xpuu3X7dv",
      couponTargetPlan: "LJjkrSrk",
    },
  ];

  PLANS_3_0 = [
    // STARTER
    {
      id: "price_1LqTqCFmAW2gX81XMxhsf8eF",
      price: 1900,
      monthlyPrice: 1900,
      title: "Hobby & Student",
      allotment: 30000,
      description: "Good for short stories, blog posts, or essays",
      allowOverage: false,
      allowPurchaseQuota: true,
      packSet: "tier1",
      graceAmount: 1000,
      defaultWormholeOutputCards: 2,
      interval: "month",
      creditTargetPlanId: "price_1OIi4CFmAW2gX81XqVbpmuOx",
    },
    {
      id: "price_1LqTqCFmAW2gX81XcRybVjN3",
      price: 12000,
      monthlyPrice: 1000,
      title: "Hobby & Student",
      allotment: 30000,
      description: "Good for short stories, blog posts, or essays",
      allowOverage: false,
      allowPurchaseQuota: true,
      packSet: "tier1",
      graceAmount: 1000,
      defaultWormholeOutputCards: 2,
      interval: "year",
      creditTargetPlanId: "price_1OIi4CFmAW2gX81XXPhMnsRs",
    },

    // PRO
    {
      id: "price_1LqTqAFmAW2gX81X5TxWGDyh",
      price: 2900,
      monthlyPrice: 2900,
      title: "Professional",
      allotment: 90000,
      description: "Good for longer works, like a novel or screenplay",
      allowOverage: false,
      graceAmount: 1000,
      allowPurchaseQuota: true,
      packSet: "tier2",
      defaultWormholeOutputCards: 3,
      interval: "month",
      popular: true,
      doubled: true, // whether to show the doubled strikethrough in quota display
      creditTargetPlanId: "price_1OIi9JFmAW2gX81Xx9UIdO4d",
    },
    {
      id: "price_1LqTqAFmAW2gX81XRdXOCkK6",
      price: 24000,
      monthlyPrice: 2000,
      title: "Professional",
      allotment: 90000,
      description: "Good for longer works, like a novel or screenplay",
      allowOverage: false,
      graceAmount: 1000,
      allowPurchaseQuota: true,
      packSet: "tier2",
      defaultWormholeOutputCards: 3,
      interval: "year",
      popular: true,
      doubled: true, // whether to show the doubled strikethrough in quota display
      creditTargetPlanId: "price_1OIi9JFmAW2gX81XA5hYVn7m",
    },

    // MAX
    {
      id: "price_1LqTpMFmAW2gX81XUZxLKEWY",
      title: "Max",
      allotment: 300000,
      description: "For those looking to work AI deeply into their process",
      price: 12900,
      monthlyPrice: 12900,
      graceAmount: 0,
      allowOverage: false,
      allowPurchaseQuota: true,
      packSet: "tier3",
      defaultWormholeOutputCards: 4,
      interval: "month",
      creditTargetPlanId: "price_1OIiBxFmAW2gX81Xpuu3X7dv",
    },
    {
      id: "price_1LqTpMFmAW2gX81XgEXdwTtt",
      title: "Max",
      allotment: 300000,
      description: "For those looking to work AI deeply into their process",
      price: 120000,
      monthlyPrice: 10000,
      graceAmount: 0,
      allowOverage: false,
      allowPurchaseQuota: true,
      packSet: "tier3",
      defaultWormholeOutputCards: 4,
      interval: "year",
      creditTargetPlanId: "price_1OIiBxFmAW2gX81X0KIvt0bJ",
    },
  ];

  PLANS_4_0 = [
    // STARTER
    {
      id: "price_1OIguAFmAW2gX81X2WcuXZan",
      price: 1900,
      monthlyPrice: 1900,
      title: "Hobby & Student",
      allotment: 225_000,
      description: "Perfect for people who write for fun or for school",
      allowOverage: false,
      allowPurchaseQuota: true,
      packSet: "tier1",
      graceAmount: 15_000,
      firstMonthGrace: 75_000,
      defaultWormholeOutputCards: 2,
      interval: "month",
      usesCredits: true,
    },
    {
      id: "price_1OIguAFmAW2gX81XtH1SerYc",
      price: 12000,
      monthlyPrice: 1000,
      title: "Hobby & Student",
      allotment: 225_000,
      description: "Perfect for people who write for fun or for school",
      allowOverage: false,
      allowPurchaseQuota: true,
      packSet: "tier1",
      graceAmount: 15_000,
      firstMonthGrace: 75_000,
      defaultWormholeOutputCards: 2,
      interval: "year",
      usesCredits: true,
    },

    // Shadow plans for legacy users
    {
      id: "price_1OIi4CFmAW2gX81XqVbpmuOx",
      price: 1900,
      monthlyPrice: 1900,
      title: "Hobby & Student (legacy with bonus)",
      allotment: 300_000,
      description: "Perfect for people who write for fun or for school",
      allowOverage: false,
      allowPurchaseQuota: true,
      packSet: "tier1",
      graceAmount: 15_000,
      firstMonthGrace: 75_000,
      defaultWormholeOutputCards: 2,
      interval: "month",
      hidden: true, // whether to display in the offerings
      usesCredits: true,
    },
    {
      id: "price_1OIi4CFmAW2gX81XXPhMnsRs",
      price: 12000,
      monthlyPrice: 1000,
      title: "Hobby & Student (legacy with bonus)",
      allotment: 300_000,
      description: "Perfect for people who write for fun or for school",
      allowOverage: false,
      allowPurchaseQuota: true,
      packSet: "tier1",
      graceAmount: 15_000,
      firstMonthGrace: 75_000,
      defaultWormholeOutputCards: 2,
      interval: "year",
      hidden: true, // whether to display in the offerings
      usesCredits: true,
    },

    // PRO
    {
      id: "price_1OIh83FmAW2gX81XVRi1FIWJ",
      price: 2900,
      monthlyPrice: 2900,
      title: "Professional",
      allotment: 1_000_000,
      description: "Good for longer works, like a novel or screenplay",
      allowOverage: false,
      graceAmount: 15_000,
      firstMonthGrace: 100_000,
      allowPurchaseQuota: true,
      packSet: "tier2",
      defaultWormholeOutputCards: 3,
      interval: "month",
      popular: true,
      crossedOutAllotment: 450_000,
      usesCredits: true,
    },

    {
      id: "price_1OIh83FmAW2gX81XHwm73q4g",
      price: 26400,
      monthlyPrice: 2200,
      title: "Professional",
      allotment: 1_000_000,
      description: "Good for longer works, like a novel or screenplay",
      allowOverage: false,
      graceAmount: 15_000,
      firstMonthGrace: 100_000,
      allowPurchaseQuota: true,
      packSet: "tier2",
      defaultWormholeOutputCards: 3,
      interval: "year",
      popular: true,
      crossedOutAllotment: 450_000,
      usesCredits: true,
    },

    // Shadow plans for legacy users
    {
      id: "price_1OIi9JFmAW2gX81Xx9UIdO4d",
      price: 2900,
      monthlyPrice: 2900,
      title: "Professional (legacy with bonus)",
      allotment: 1_100_000,
      description: "Good for longer works, like a novel or screenplay",
      allowOverage: false,
      graceAmount: 15_000,
      firstMonthGrace: 100_000,
      allowPurchaseQuota: true,
      packSet: "tier2",
      defaultWormholeOutputCards: 3,
      interval: "month",
      popular: true,
      hidden: true, // whether to display in the offerings
      crossedOutAllotment: 450_000,
      usesCredits: true,
    },
    {
      id: "price_1OIi9JFmAW2gX81XA5hYVn7m",
      price: 24000,
      monthlyPrice: 2000,
      title: "Professional (legacy with bonus)",
      allotment: 1_100_000,
      description: "Good for longer works, like a novel or screenplay",
      allowOverage: false,
      graceAmount: 15_000,
      firstMonthGrace: 100_000,
      allowPurchaseQuota: true,
      packSet: "tier2",
      defaultWormholeOutputCards: 3,
      interval: "year",
      popular: true,
      hidden: true, // whether to display in the offerings
      crossedOutAllotment: 450_000,
      usesCredits: true,
    },
    {
      // TODO: in the transition, this plan should get extended by 2 months
      id: "price_1OIi9JFmAW2gX81X5jKdJenr",
      price: 26400,
      monthlyPrice: 2200,
      title: "Professional (legacy with bonus)",
      allotment: 1_100_000,
      description: "Good for longer works, like a novel or screenplay",
      allowOverage: false,
      graceAmount: 15_000,
      firstMonthGrace: 100_000,
      allowPurchaseQuota: true,
      packSet: "tier2",
      defaultWormholeOutputCards: 3,
      interval: "year",
      popular: true,
      hidden: true, // whether to display in the offerings
      crossedOutAllotment: 450_000,
      usesCredits: true,
    },

    // MAX
    {
      id: "price_1OIhlVFmAW2gX81X02ItSycg",
      title: "Max",
      allotment: 2_000_000,
      description: "For authors who publish multiple times a year",
      price: 5900,
      monthlyPrice: 5900,
      graceAmount: 15_000,
      firstMonthGrace: 100_000,
      allowOverage: false,
      allowPurchaseQuota: true,
      packSet: "tier3",
      defaultWormholeOutputCards: 4,
      interval: "month",
      rollover: true, // monthly allotment rolls over for 12 months
      usesCredits: true,
    },
    {
      id: "price_1OIhlVFmAW2gX81XHMp2tkkH",
      title: "Max",
      allotment: 2_000_000,
      description: "For authors who publish multiple times a year",
      price: 52800,
      monthlyPrice: 4400,
      graceAmount: 15_000,
      firstMonthGrace: 100_000,
      allowOverage: false,
      allowPurchaseQuota: true,
      packSet: "tier3",
      defaultWormholeOutputCards: 4,
      interval: "year",
      rollover: true, // monthly allotment rolls over for 12 months
      usesCredits: true,
    },

    // Shadow plans for legacy users
    {
      id: "price_1OIiBxFmAW2gX81Xpuu3X7dv",
      title: "Super Max (legacy with bonus)",
      allotment: 5_000_000,
      description: "For authors who publish multiple times a year",
      price: 12900,
      monthlyPrice: 12900,
      graceAmount: 15_000,
      firstMonthGrace: 100_000,
      allowOverage: false,
      allowPurchaseQuota: true,
      packSet: "tier3",
      defaultWormholeOutputCards: 4,
      interval: "month",
      rollover: true, // monthly allotment rolls over for 12 months
      hidden: true, // whether to display in the offerings
      usesCredits: true,
    },
    {
      id: "price_1OIiBxFmAW2gX81X0KIvt0bJ",
      title: "Super Max (legacy with bonus)",
      allotment: 5_000_000,
      description: "For authors who publish multiple times a year",
      price: 120000,
      monthlyPrice: 10000,
      graceAmount: 15_000,
      firstMonthGrace: 100_000,
      allowOverage: false,
      allowPurchaseQuota: true,
      packSet: "tier3",
      defaultWormholeOutputCards: 4,
      interval: "year",
      rollover: true, // monthly allotment rolls over for 12 months
      hidden: true, // whether to display in the offerings
      usesCredits: true,
    },

    // SPECIAL PRO PLAN - used with people who cancel due to no rollover
    {
      id: "price_1OIiDmFmAW2gX81XbPLwiBC9",
      title: "Professional Rollover",
      allotment: 500_000,
      description: "Good for longer works, like a novel or screenplay",
      price: 2900,
      monthlyPrice: 2900,
      graceAmount: 15_000,
      firstMonthGrace: 100_000,
      allowOverage: false,
      allowPurchaseQuota: true,
      packSet: "tier2",
      defaultWormholeOutputCards: 4,
      interval: "month",
      rollover: true, // monthly allotment rolls over for 12 months
      hidden: true, // whether to display in the offerings
      usesCredits: true,
    },
    {
      id: "price_1OIiDmFmAW2gX81XI78dFlLh",
      title: "Professional Rollover",
      allotment: 500_000,
      description: "Good for longer works, like a novel or screenplay",
      price: 26400,
      monthlyPrice: 2200,
      graceAmount: 15_000,
      firstMonthGrace: 100_000,
      allowOverage: false,
      allowPurchaseQuota: true,
      packSet: "tier2",
      defaultWormholeOutputCards: 4,
      interval: "year",
      rollover: true, // monthly allotment rolls over for 12 months
      hidden: true, // whether to display in the offerings
      usesCredits: true,
    },
  ];
} else {
  // DEVELOPMENT

  PACKS = {
    // 2.0 $99 Plan
    legacy: [
      {
        name: "Small",
        quota: 25000,
        id: "price_1LHHbiFmAW2gX81X6SnknSNW",
        amount: 1000,
      },
      {
        name: "Medium",
        quota: 62500,
        id: "price_1LHJBNFmAW2gX81XaQo3fUBs",
        amount: 2500,
      },
      {
        name: "Large",
        quota: 125000,
        id: "price_1LHJBnFmAW2gX81XabPtRumO",
        amount: 5000,
      },
      {
        name: "XL",
        quota: 250000,
        id: "price_1LpKVwFmAW2gX81XQSnPzwwV",
        amount: 10000,
      },
    ],
    tier1: [
      {
        name: "Small",
        quota: 5000,
        id: "price_1LpKSBFmAW2gX81Xwk60kETN",
        amount: 500,
      },
      {
        name: "Medium",
        quota: 10000,
        id: "price_1LpDPSFmAW2gX81XkQxQalzW",
        amount: 1000,
      },
      {
        name: "Large",
        quota: 25000,
        id: "price_1LpKTIFmAW2gX81XqjY4eMcf",
        amount: 2000,
      },
    ],
    tier2: [
      {
        name: "Small",
        id: "price_1LpDPSFmAW2gX81XkQxQalzW",
        quota: 10000,
        amount: 1000,
      },
      {
        name: "Medium",
        id: "price_1LpKTiFmAW2gX81X38aVVaFd",
        quota: 30000,
        amount: 2500,
      },
      {
        name: "Large",
        id: "price_1LpKUVFmAW2gX81XVdg7aW9J",
        quota: 75000,
        amount: 5000,
      },
      {
        name: "XL",
        id: "price_1LpKVWFmAW2gX81XuDpR6FQm",
        quota: 150000,
        amount: 9000,
      },
    ],
    tier3: [
      {
        name: "Small",
        id: "price_1LpKSfFmAW2gX81XpwoOP6ex",
        quota: 15000,
        amount: 1000,
      },
      {
        name: "Medium",
        id: "price_1LpKU8FmAW2gX81X2L8bEThp",
        quota: 40000,
        amount: 2500,
      },
      {
        name: "Large",
        id: "price_1LpKVDFmAW2gX81XOXeRhJm1",
        quota: 100000,
        amount: 5000,
      },
      {
        name: "XL",
        id: "price_1LpKVwFmAW2gX81XQSnPzwwV",
        quota: 250000,
        amount: 10000,
      },
    ],
  };

  //
  CREDIT_PACKS = {
    tier1: [
      {
        name: "Small",
        quota: 100_000,
        id: "price_1O7SL6FmAW2gX81XQJmU85H1",
        amount: 500,
      },
      {
        name: "Medium",
        quota: 500_000,
        id: "price_1O7SM3FmAW2gX81XnX4Bq5Ng",
        amount: 1900,
      },
      {
        name: "Large",
        quota: 1_750_000,
        id: "price_1O7SNVFmAW2gX81X5hMibkqg",
        amount: 4900,
      },
    ],
    tier2: [
      {
        name: "Small",
        id: "price_1O7SR3FmAW2gX81XjVjBINx2",
        quota: 200_000,
        amount: 900,
      },
      {
        name: "Medium",
        id: "price_1O7SO9FmAW2gX81XTcgxf4Lz",
        quota: 600_000,
        amount: 1900,
      },
      {
        name: "Large",
        id: "price_1O7SOsFmAW2gX81XDlc7ZuKB",
        quota: 1_900_000,
        amount: 4900,
      },
      {
        name: "XL",
        id: "price_1O7SQQFmAW2gX81XSDwnWnSB",
        quota: 4_000_000,
        amount: 9900,
      },
    ],
    tier3: [
      {
        name: "Small",
        id: "price_1O7SR3FmAW2gX81XjVjBINx2",
        quota: 200_000,
        amount: 900,
      },
      {
        name: "Medium",
        id: "price_1O7SS8FmAW2gX81XXYCLyR7R",
        quota: 1_000_000,
        amount: 2900,
      },
      {
        name: "Large",
        id: "price_1O7SSXFmAW2gX81XXaT1hocf",
        quota: 2_000_000,
        amount: 4900,
      },
      {
        name: "XL",
        id: "price_1O7STHFmAW2gX81XF7rVtm57",
        quota: 4_700_000,
        amount: 9900,
      },
      // This last pack doesn't fit in the design
      // {
      //   name: "XXL",
      //   id: "price_1OEfesFmAW2gX81XfjfavTsh",
      //   quota: 10_000_000,
      //   amount: 19900,
      // },
    ],
  };

  PLANS_1_0 = [
    {
      id: "price_1IIfOwFmAW2gX81X7WyIqEZv",
      price: 1000,
      monthlyPrice: 1000,
      title: "The Sudowrite Plan",
      allowOverage: false,
      graceAmount: 0,
      allotment: 200000,
      interval: "month",
      hidden: true, // whether to display in the offerings
      hideAllotment: true,
    },
    {
      id: "price_1IbvFVFmAW2gX81Xmd9ix9UR",
      price: 1500,
      monthlyPrice: 1500,
      title: "The Sudowrite Plan",
      allowOverage: false,
      graceAmount: 0,
      allotment: 200000,
      interval: "month",
      hidden: true,
      hideAllotment: true, // whether to hide the allotment
    },
    {
      id: "price_1IbvFaFmAW2gX81XeZa2LFrZ",
      price: 2000,
      monthlyPrice: 2000,
      title: "The Sudowrite Plan",
      description: "Our best-in-class AI writing tools",
      allowOverage: false,
      graceAmount: 0,
      allotment: 200000,
      hideAllotment: true,
      interval: "month",
    },
    {
      id: "price_1ITCEyFmAW2gX81XW0Z6Nd3I",
      price: 2500,
      monthlyPrice: 2500,
      title: "The Sudowrite Plan",
      allowOverage: false,
      graceAmount: 0,
      allotment: 200000,
      interval: "month",
      hidden: true,
      hideAllotment: true,
    },
  ];

  PLANS_2_0 = [
    // MONTHLY
    {
      id: "price_1KYZOKFmAW2gX81Xw9vWm238",
      title: "Limited Free",
      description: "Good for short stories, blog posts, or essays",
      allotment: 1000,
      price: 0,
      monthlyPrice: 0,
      allowOverage: false,
      allowPurchaseQuota: true,
      graceAmount: 100,
      defaultWormholeOutputCards: 2,
      interval: "month",
      creditTargetPlanId: "", // TODO: WHAT IS THE TARGET HERE?
    },
    {
      id: "price_1KfqaHFmAW2gX81Xn9jnuegu",
      price: 999,
      monthlyPrice: 999,
      title: "Hobby & Student",
      allotment: 10000,
      description: "Good for short stories, blog posts, or essays",
      allowOverage: false,
      allowPurchaseQuota: true,
      graceAmount: 1000,
      defaultWormholeOutputCards: 2,
      interval: "month",
      creditTargetPlanId: "price_1OEj4ZFmAW2gX81XsQ6Em8nV",
      couponTargetPlan: "a0SCUA7x",
    },
    {
      id: "price_1KYZRjFmAW2gX81XEyJ89pHp",
      price: 2900,
      monthlyPrice: 2900,
      title: "Professional",
      allotment: 50000,
      description: "Good for longer works, like a novel or screenplay",
      allowOverage: false,
      graceAmount: 1000,
      allowPurchaseQuota: true,
      defaultWormholeOutputCards: 3,
      interval: "month",
      popular: true,
      creditTargetPlanId: "price_1O7SC0FmAW2gX81XVzgMxENF",
    },
    {
      id: "price_1KYZRvFmAW2gX81XPlpUU2CV",
      title: "Professional+",
      allotment: 200000,
      description: "For those looking to work AI deeply into their process",
      price: 9900,
      monthlyPrice: 9900,
      graceAmount: 0,
      allowOverage: false,
      allowPurchaseQuota: true,
      packSet: "legacy",
      defaultWormholeOutputCards: 4,
      interval: "month",
      creditTargetPlanId: "price_1OEjEMFmAW2gX81XAmfgTYOi",
      couponTargetPlan: "LJjkrSrk",
    },
  ];

  PLANS_3_0 = [
    // STARTER
    {
      id: "price_1LmgotFmAW2gX81XUESoTX4d",
      price: 1900,
      monthlyPrice: 1900,
      title: "Hobby & Student",
      allotment: 30000,
      description: "Good for short stories, blog posts, or essays",
      allowOverage: false,
      allowPurchaseQuota: true,
      packSet: "tier1",
      graceAmount: 1000,
      defaultWormholeOutputCards: 2,
      interval: "month",
      creditTargetPlanId: "price_1OEj4ZFmAW2gX81XsQ6Em8nV",
    },
    {
      id: "price_1LmgotFmAW2gX81X8BaXYb9X",
      price: 12000,
      monthlyPrice: 1000,
      title: "Hobby & Student",
      allotment: 30000,
      description: "Good for short stories, blog posts, or essays",
      allowOverage: false,
      allowPurchaseQuota: true,
      packSet: "tier1",
      graceAmount: 1000,
      defaultWormholeOutputCards: 2,
      interval: "year",
      creditTargetPlanId: "price_1OEj4ZFmAW2gX81X44F9OL54",
    },

    // PRO
    {
      id: "price_1LmgrRFmAW2gX81X2mEA4gX1",
      price: 2900,
      monthlyPrice: 2900,
      title: "Professional",
      allotment: 90000,
      description: "Good for longer works, like a novel or screenplay",
      allowOverage: false,
      graceAmount: 1000,
      allowPurchaseQuota: true,
      packSet: "tier2",
      defaultWormholeOutputCards: 3,
      interval: "month",
      popular: true,
      doubled: true, // whether to show the doubled strikethrough in quota display
      creditTargetPlanId: "price_1OEj9bFmAW2gX81XZCRYkyX5",
    },

    {
      id: "price_1LmgrRFmAW2gX81XXxQzjWn6",
      price: 24000,
      monthlyPrice: 2000,
      title: "Professional",
      allotment: 90000,
      description: "Good for longer works, like a novel or screenplay",
      allowOverage: false,
      graceAmount: 1000,
      allowPurchaseQuota: true,
      packSet: "tier2",
      defaultWormholeOutputCards: 3,
      interval: "year",
      popular: true,
      doubled: true, // whether to show the doubled strikethrough in quota display
      creditTargetPlanId: "price_1OEj9bFmAW2gX81XpXAijjv9",
    },

    // MAX
    {
      id: "price_1LmltxFmAW2gX81Xw3F28usK",
      title: "Max",
      allotment: 300000,
      description: "For those looking to work AI deeply into their process",
      price: 12900,
      monthlyPrice: 12900,
      graceAmount: 0,
      allowOverage: false,
      allowPurchaseQuota: true,
      packSet: "tier3",
      defaultWormholeOutputCards: 4,
      interval: "month",
      creditTargetPlanId: "price_1OEjEMFmAW2gX81XdBkr80tk",
    },
    {
      id: "price_1LmluBFmAW2gX81Xo2L6l4T8",
      title: "Max",
      allotment: 300000,
      description: "For those looking to work AI deeply into their process",
      price: 120000,
      monthlyPrice: 10000,
      graceAmount: 0,
      allowOverage: false,
      allowPurchaseQuota: true,
      packSet: "tier3",
      defaultWormholeOutputCards: 4,
      interval: "year",
      creditTargetPlanId: "price_1OEjEMFmAW2gX81XAmfgTYOi",
    },
  ];

  PLANS_4_0 = [
    // STARTER
    {
      id: "price_1O7S9LFmAW2gX81Xqqhn2C7D",
      price: 1900,
      monthlyPrice: 1900,
      title: "Hobby & Student",
      allotment: 225_000,
      description: "Perfect for people who write for fun or for school",
      allowOverage: false,
      allowPurchaseQuota: true,
      packSet: "tier1",
      graceAmount: 15_000,
      firstMonthGrace: 75_000,
      defaultWormholeOutputCards: 2,
      interval: "month",
      usesCredits: true,
    },
    {
      id: "price_1O7S9LFmAW2gX81XCV42aH3V",
      price: 12000,
      monthlyPrice: 1000,
      title: "Hobby & Student",
      allotment: 225_000,
      description: "Perfect for people who write for fun or for school",
      allowOverage: false,
      allowPurchaseQuota: true,
      packSet: "tier1",
      graceAmount: 15_000,
      firstMonthGrace: 75_000,
      defaultWormholeOutputCards: 2,
      interval: "year",
      usesCredits: true,
    },

    // Shadow plans for legacy users
    {
      id: "price_1OEj4ZFmAW2gX81XsQ6Em8nV",
      price: 1900,
      monthlyPrice: 1900,
      title: "Hobby & Student (legacy with bonus)",
      allotment: 250_000,
      description: "Perfect for people who write for fun or for school",
      allowOverage: false,
      allowPurchaseQuota: true,
      packSet: "tier1",
      graceAmount: 15_000,
      firstMonthGrace: 75_000,
      defaultWormholeOutputCards: 2,
      interval: "month",
      hidden: true, // whether to display in the offerings
      usesCredits: true,
    },
    {
      id: "price_1OEj4ZFmAW2gX81X44F9OL54",
      price: 12000,
      monthlyPrice: 1000,
      title: "Hobby & Student (legacy with bonus)",
      allotment: 250_000,
      description: "Perfect for people who write for fun or for school",
      allowOverage: false,
      allowPurchaseQuota: true,
      packSet: "tier1",
      graceAmount: 15_000,
      firstMonthGrace: 75_000,
      defaultWormholeOutputCards: 2,
      interval: "year",
      hidden: true, // whether to display in the offerings
      usesCredits: true,
    },

    // PRO
    {
      id: "price_1O7SC0FmAW2gX81XVzgMxENF",
      price: 2900,
      monthlyPrice: 2900,
      title: "Professional",
      allotment: 1_000_000,
      description: "Good for longer works, like a novel or screenplay",
      allowOverage: false,
      graceAmount: 15_000,
      firstMonthGrace: 100_000,
      allowPurchaseQuota: true,
      packSet: "tier2",
      defaultWormholeOutputCards: 3,
      interval: "month",
      popular: true,
      crossedOutAllotment: 450_000,
      usesCredits: true,
    },

    {
      id: "price_1O7SC0FmAW2gX81XcRP9XM7T",
      price: 26400,
      monthlyPrice: 2200,
      title: "Professional",
      allotment: 1_000_000,
      description: "Good for longer works, like a novel or screenplay",
      allowOverage: false,
      graceAmount: 15_000,
      firstMonthGrace: 100_000,
      allowPurchaseQuota: true,
      packSet: "tier2",
      defaultWormholeOutputCards: 3,
      interval: "year",
      popular: true,
      crossedOutAllotment: 450_000,
      usesCredits: true,
    },

    // Shadow plans for legacy users
    {
      id: "price_1OEj9bFmAW2gX81XZCRYkyX5",
      price: 2900,
      monthlyPrice: 2900,
      title: "Professional (legacy with bonus)",
      allotment: 1_100_000,
      description: "Good for longer works, like a novel or screenplay",
      allowOverage: false,
      graceAmount: 15_000,
      firstMonthGrace: 100_000,
      allowPurchaseQuota: true,
      packSet: "tier2",
      defaultWormholeOutputCards: 3,
      interval: "month",
      popular: true,
      hidden: true, // whether to display in the offerings
      crossedOutAllotment: 450_000,
      usesCredits: true,
    },
    {
      id: "price_1OEj9bFmAW2gX81XpXAijjv9",
      price: 24000,
      monthlyPrice: 2000,
      title: "Professional (legacy with bonus)",
      allotment: 1_100_000,
      description: "Good for longer works, like a novel or screenplay",
      allowOverage: false,
      graceAmount: 15_000,
      firstMonthGrace: 100_000,
      allowPurchaseQuota: true,
      packSet: "tier2",
      defaultWormholeOutputCards: 3,
      interval: "year",
      popular: true,
      hidden: true, // whether to display in the offerings
      crossedOutAllotment: 450_000,
      usesCredits: true,
    },
    // TODO: in the transition, this plan should get extended by 2 months
    {
      id: "price_1OEj9bFmAW2gX81XM4orBWsU",
      price: 26400,
      monthlyPrice: 2200,
      title: "Professional (legacy with bonus)",
      allotment: 1_100_000,
      description: "Good for longer works, like a novel or screenplay",
      allowOverage: false,
      graceAmount: 15_000,
      firstMonthGrace: 100_000,
      allowPurchaseQuota: true,
      packSet: "tier2",
      defaultWormholeOutputCards: 3,
      interval: "year",
      popular: true,
      hidden: true, // whether to display in the offerings
      crossedOutAllotment: 450_000,
      usesCredits: true,
    },

    // MAX
    {
      id: "price_1OEf3zFmAW2gX81Xbf5dKoZN",
      title: "Max",
      allotment: 2_000_000,
      description: "For authors who publish multiple times a year",
      price: 5900,
      monthlyPrice: 5900,
      graceAmount: 15_000,
      firstMonthGrace: 100_000,
      allowOverage: false,
      allowPurchaseQuota: true,
      packSet: "tier3",
      defaultWormholeOutputCards: 4,
      interval: "month",
      rollover: true, // monthly allotment rolls over for 12 months
      usesCredits: true,
    },
    {
      id: "price_1O7SE5FmAW2gX81XLIIO6sSW",
      title: "Max",
      allotment: 2_000_000,
      description: "For authors who publish multiple times a year",
      price: 52800,
      monthlyPrice: 4400,
      graceAmount: 15_000,
      firstMonthGrace: 100_000,
      allowOverage: false,
      allowPurchaseQuota: true,
      packSet: "tier3",
      defaultWormholeOutputCards: 4,
      interval: "year",
      rollover: true, // monthly allotment rolls over for 12 months
      usesCredits: true,
    },

    // Shadow plans for legacy users
    {
      id: "price_1OEjEMFmAW2gX81XdBkr80tk",
      title: "Super Max (legacy with bonus)",
      allotment: 5_000_000,
      description: "For authors who publish multiple times a year",
      price: 12900,
      monthlyPrice: 12900,
      graceAmount: 15_000,
      firstMonthGrace: 100_000,
      allowOverage: false,
      allowPurchaseQuota: true,
      packSet: "tier3",
      defaultWormholeOutputCards: 4,
      interval: "month",
      rollover: true, // monthly allotment rolls over for 12 months
      hidden: true, // whether to display in the offerings
      usesCredits: true,
    },
    {
      id: "price_1OEjEMFmAW2gX81XAmfgTYOi",
      title: "Super Max (legacy with bonus)",
      allotment: 5_000_000,
      description: "For authors who publish multiple times a year",
      price: 120000,
      monthlyPrice: 10000,
      graceAmount: 15_000,
      firstMonthGrace: 100_000,
      allowOverage: false,
      allowPurchaseQuota: true,
      packSet: "tier3",
      defaultWormholeOutputCards: 4,
      interval: "year",
      rollover: true, // monthly allotment rolls over for 12 months
      hidden: true, // whether to display in the offerings
      usesCredits: true,
    },

    // SPECIAL PRO PLAN - used with people who cancel due to no rollover
    {
      id: "price_1OEjMkFmAW2gX81XeeXtZgZP",
      title: "Professional Rollover",
      allotment: 500_000,
      description: "Good for longer works, like a novel or screenplay",
      price: 2900,
      monthlyPrice: 2900,
      graceAmount: 15_000,
      firstMonthGrace: 100_000,
      allowOverage: false,
      allowPurchaseQuota: true,
      packSet: "tier2",
      defaultWormholeOutputCards: 4,
      interval: "month",
      rollover: true, // monthly allotment rolls over for 12 months
      hidden: true, // whether to display in the offerings
      usesCredits: true,
    },
    {
      id: "price_1OEjMkFmAW2gX81XoD4Uh63c",
      title: "Professional Rollover",
      allotment: 500_000,
      description: "Good for longer works, like a novel or screenplay",
      price: 26400,
      monthlyPrice: 2200,
      graceAmount: 15_000,
      firstMonthGrace: 100_000,
      allowOverage: false,
      allowPurchaseQuota: true,
      packSet: "tier2",
      defaultWormholeOutputCards: 4,
      interval: "year",
      rollover: true, // monthly allotment rolls over for 12 months
      hidden: true, // whether to display in the offerings
      usesCredits: true,
    },
  ];
}

// This plan supersedes the $20 / month annual plan
const PLAN_25_PER_MONTH_ANNUAL =
  process.env.NODE_ENV === "production"
    ? {
        id: "price_1N7tCCFmAW2gX81XxAVZFgrE",
        price: 30000,
        monthlyPrice: 2500,
        title: "Professional",
        allotment: 90000,
        description: "Good for longer works, like a novel or screenplay",
        allowOverage: false,
        graceAmount: 1000,
        allowPurchaseQuota: true,
        packSet: "tier2",
        defaultWormholeOutputCards: 3,
        interval: "year",
        popular: true,
        doubled: true, // whether to show the doubled strikethrough in quota display
        creditTargetPlanId: "price_1OIi9JFmAW2gX81X5jKdJenr",
        onConversionCreditCompensation: true, // whether to compensate on conversion to v4
      }
    : {
        id: "price_1N7t46FmAW2gX81XiykuFMpi",
        price: 30000,
        monthlyPrice: 2500,
        title: "Professional",
        allotment: 90000,
        description: "Good for longer works, like a novel or screenplay",
        allowOverage: false,
        graceAmount: 1000,
        allowPurchaseQuota: true,
        packSet: "tier2",
        defaultWormholeOutputCards: 3,
        interval: "year",
        popular: true,
        doubled: true, // whether to show the doubled strikethrough in quota display
        creditTargetPlanId: "price_1OEj9bFmAW2gX81XM4orBWsU", // TODO: verify this
        onConversionCreditCompensation: true, // whether to compensate on conversion to v4
      };

// 2.1 excludes the free plan
const PLANS_2_1 = nestedCopy(PLANS_2_0).filter((plan) => plan.price !== 0);
const PLANS_2_3 = nestedCopy(PLANS_2_1);
PLANS_2_3.find((plan) => plan.price === 9900).allowOverage = true;

// 3.1 has a new Pro annual plan
const PLANS_3_1 = nestedCopy(PLANS_3_0).map((plan) => {
  if (plan.title === "Professional" && plan.interval === "year") {
    return PLAN_25_PER_MONTH_ANNUAL;
  } else {
    return plan;
  }
});

export const PRICING_PLANS = {
  "1.0": {
    plans: nestedCopy(PLANS_1_0),
  },
  // with free trial
  "2.0": {
    trialQuota: 4000,
    trialGrace: 1000,
    plans: nestedCopy(PLANS_2_0),
  },
  // 4000 trial quota
  2.1: {
    trialQuota: 4000,
    trialGrace: 1000,
    plans: nestedCopy(PLANS_2_1),
  },
  // 8000 trial quota
  2.2: {
    trialQuota: 8000,
    trialGrace: 1000,
    plans: nestedCopy(PLANS_2_1),
  },
  // $99 plan that allows overage
  2.3: {
    trialQuota: 8000,
    trialGrace: 1000,
    plans: nestedCopy(PLANS_2_3),
  },
  // 3.0 Plans
  "3.0": {
    trialQuota: 4000,
    trialGrace: 1000,
    plans: nestedCopy(PLANS_3_0),
  },
  // 3.1 Plans
  3.1: {
    trialQuota: 4000,
    trialGrace: 1000,
    plans: nestedCopy(PLANS_3_1),
  },
  // 4.0 Plans
  "4.0": {
    trialQuota: 50000,
    trialGrace: 15000,
    usesLedgers: true,
    plans: nestedCopy(PLANS_4_0),
  },
  4.1: {
    trialQuota: 20000,
    trialGrace: 5000,
    usesLedgers: true,
    plans: nestedCopy(PLANS_4_0),
  },
  4.2: {
    trialQuota: 10000,
    trialGrace: 5000,
    usesLedgers: true,
    plans: nestedCopy(PLANS_4_0),
  },
};

/** Adds version field to all plan objects within each version */
Object.keys(PRICING_PLANS).forEach((version) => {
  PRICING_PLANS[version].plans.forEach((value, i) => {
    PRICING_PLANS[version].plans[i].version = version;
  });
});

/** All plan ids in a single array */
export const ALL_PLAN_IDS = Object.keys(PRICING_PLANS)
  .map((key) => {
    return PRICING_PLANS[key].plans.map((plan) => plan.id);
  })
  .flat();

/**
 * Determines which transition to use given current and target plan
 * @param {Object} currentPlan
 * @param {Object} targetPlan
 */
export function getPlanTransitionType(currentPlan, targetPlan) {
  if (targetPlan.version[0] === currentPlan.version[0]) {
    // Transitioning plans within same version

    if (currentPlan.interval === targetPlan.interval) {
      // same intervals
      if (currentPlan.price > targetPlan.price) {
        // Downgrade
        return PLAN_TRANSITION_TYPE.CANCEL_AT_PERIOD_END_AND_SCHEDULE_TRANSITION;
      } else {
        // Upgrade
        return PLAN_TRANSITION_TYPE.PRORATED_REFUND_AND_TRANSITION_IMMEDIATELY;
      }
    } else {
      // different intervals
      if (currentPlan.interval === "year" && targetPlan.interval === "month") {
        // Year => Month
        return PLAN_TRANSITION_TYPE.CANCEL_AT_PERIOD_END_AND_SCHEDULE_TRANSITION;
      } else {
        // Month => Year
        return PLAN_TRANSITION_TYPE.PRORATED_REFUND_AND_TRANSITION_IMMEDIATELY;
      }
    }
  } else {
    // Transitioning plans between DIFFERENT versions
    if (targetPlan.version[0] !== "3" && targetPlan.version[0] !== "4") {
      throw new Error("Not allowed to transition to any versions besides 3.x and 4.x");
    }

    if (currentPlan.version[0] === "1") {
      return PLAN_TRANSITION_TYPE.PRORATED_REFUND_AND_TRANSITION_IMMEDIATELY;
    } else if (currentPlan.version[0] === "2" || currentPlan.version[0] === "3") {
      if (targetPlan.version[0] === "3") {
        if (currentPlan.interval === targetPlan.interval) {
          return PLAN_TRANSITION_TYPE.TRANSITION_NATURALLY;
        } else {
          return PLAN_TRANSITION_TYPE.PRORATED_REFUND_AND_TRANSITION_IMMEDIATELY;
        }
      } else {
        // for V4, we always transition immediately
        return PLAN_TRANSITION_TYPE.PRORATED_REFUND_AND_TRANSITION_IMMEDIATELY;
      }
    } else {
      throw new Error(`Invalid version transition: from ${currentPlan.version} to ${targetPlan.version}`);
    }
  }
}

export function planToString(plan) {
  if (!plan) {
    return "No plan";
  } else {
    return `${plan.title} ($${plan.price / 100}, ${plan.interval}ly, v${plan.version})`;
  }
}

export const DEFAULT_PRICING_VERSION = "4.2";
export const PRICING_V4_TEST_KEY = "test_pricing_v4_3";
export const TRIAL_CREDITS_TEST_KEY = "test_trial_credits"; // we can reinstate this later if we want to do another trial credits test
export const TRIAL_CREDITS_TEST_VARIATIONS = ["4.1", "4.2"]; // can be used for 👆

export const PRICING_PACKS = PACKS;
export const PRICING_CREDIT_PACKS = CREDIT_PACKS;

export const OVERAGE_COST = 40; // // cents per 1000 words

export const REWARDFUL_BONUS_WORDS = 10000;

export const CHEESEY_JOKES = [
  {
    setup: "why don't escaped convicts make good writers?",
    punchline: "Because they never finish their sentences.",
  },
  {
    setup: "what state has the most writers?",
    punchline: "Pencil-vania.",
  },
  {
    setup: "how many Buzzfeed writers does it take to turn on an electric chair?",
    punchline: "Ten. But number four will shock you.",
  },
  {
    setup: "what dinosaur is a writer's best friend?",
    punchline: "The Thesaurus.",
  },
  {
    setup: "what brand of camera does a fan-fiction writer own?",
    punchline: "A Canon Camera.",
  },
  {
    setup: "what do you call a place where everyone's a writer?",
    punchline: "A writer's block.",
  },
  {
    setup: "what do you call a fight between two writers?",
    punchline: "A scribble-scrabble. (Written by Sudowrite!)",
  },
  {
    setup: "what do you call a bad writer?",
    punchline: "Malcontent. (Written by Sudowrite!)",
  },
  {
    setup: "why did the writer go to the bar?",
    punchline: "Because she wanted to get pub-lished. (Written by Sudowrite!)",
  },
  {
    setup: "how many mystery writers does it take to change a light bulb?",
    punchline: "Two. One to change the bulb, and one to figure out whodunit. (Written by Sudowrite!)",
  },
];

export const ENGINE_SETTINGS = {
  [WRITE_MODE_GUIDED.value]: [
    {
      engine: "text-davinci-003",
      temperature: 0.6,
      presence_penalty: 0.5,
    },
    {
      engine: "text-davinci-003",
      temperature: 0.7,
      presence_penalty: 0.5,
    },
    {
      engine: "text-davinci-003",
      temperature: 0.8,
      presence_penalty: 0.5,
    },
    {
      engine: "text-davinci-003",
      temperature: 0.9,
      presence_penalty: 0.5,
    },
    {
      engine: "text-davinci-003",
      temperature: 0.94,
      presence_penalty: 0.5,
      frequency_penalty: 0,
    },
  ],
  [WRITE_MODE_TONE.value]: [
    {
      engineChat: "gpt-4o-mini-2024-07-18",
      engineCompletion: "text-davinci-003",
      temperature: 0.6,
      presence_penalty: 0.5,
    },
    {
      engineChat: "gpt-4o-mini-2024-07-18",
      engineCompletion: "text-davinci-003",
      temperature: 0.7,
      presence_penalty: 0.5,
    },
    {
      engineChat: "gpt-4o-mini-2024-07-18",
      engineCompletion: "text-davinci-003",
      temperature: 0.8,
      presence_penalty: 0.5,
    },
    {
      engineChat: "gpt-4o-mini-2024-07-18",
      engineCompletion: "text-davinci-003",
      temperature: 0.9,
      presence_penalty: 0.5,
    },
    {
      engineChat: "gpt-4o-mini-2024-07-18",
      engineCompletion: "text-davinci-003",
      temperature: 0.94,
      presence_penalty: 0.5,
      frequency_penalty: 0,
    },
  ],
  [WRITE_MODE_AUTO.value]: [
    {
      engineChat: "gpt-4o-mini-2024-07-18",
      engineCompletion: "text-davinci-003",
      temperature: 0.73,
    },
    {
      engineChat: "gpt-4o-mini-2024-07-18",
      engineCompletion: "davinci",
      temperature: 0.75,
    },
    {
      engineChat: "gpt-4o-mini-2024-07-18",
      engineCompletion: "davinci",
      temperature: 0.82,
    },
    {
      engineChat: "gpt-4o-mini-2024-07-18",
      engineCompletion: "text-davinci-003",
      temperature: 0.95,
    },
    {
      engineChat: "gpt-4o-mini-2024-07-18",
      engineCompletion: "davinci",
      temperature: 0.92,
      frequency_penalty: 0,
      presence_penalty: 0.2,
    },
  ],
};

export const DEFAULT_WRITE_MODEL = "excellent";
export const DEFAULT_ENGINE_OPTIONS = ENGINE_SETTINGS[DEFAULT_WRITE_MODE.value];
// Set engine settings and creativity slider to midpoint
export const DEFAULT_CREATIVITY_SLIDER_POSITION = Math.floor((DEFAULT_ENGINE_OPTIONS.length - 1) / 2);
export const DEFAULT_ENGINE_SETTINGS = DEFAULT_ENGINE_OPTIONS[DEFAULT_CREATIVITY_SLIDER_POSITION];

// Error codes
export const CONTENT_POLICY_ERROR_CODE = 401;
export const CONTENT_POLICY_ERROR = "CONTENT_POLICY_ERROR";
export const CONTENT_GENERATION_FAILED = "CONTENT_GENERATION_FAILED";
export const MULTIPLE_EMAILS_ASSOCIATED_WITH_SUBSCRIPTION_ERROR =
  "MULTIPLE_EMAILS_ASSOCIATED_WITH_SUBSCRIPTION";

export const PLUGIN_CREATORS = [
  {
    name: "Bernie Su",
    firstName: "Bernie",
    slug: "bernie",
    description:
      "Bernie Su is a Peabody and three-time Primetime Emmy award-winning creator, showrunner, and producer of the Lizzie Bennet Diaries, Emma Approved, and Artificial. He constantly challenges and embraces new technologies, methods, and platforms into his work.",
    backgroundImage: "/assets/creators/bernie-background.png",
    portrait: "/assets/creators/bernie.png",
    trayColor: "#064371",
    textColor: "#fff",
    transparentColor: "rgba(217,217,217,.2)",
    urls: [
      {
        title: "Bernie's Website",
        href: "https://www.berniesu.com",
      },
      {
        title: "Bernie's Website",
        href: "https://www.berniesu.com",
      },
    ],
    plugins: ["29NdPCT4r", "yQW6eZwU5", "F7a2QyvcX", "2PCVfo9nR", "TMCcpt477", "rRaCe5ShZ"],
  },
  {
    name: "David Kadavy",
    firstName: "David",
    slug: "david",
    description:
      "David Kadavy's best-selling books include Mind Management, Not Time Management, The Heart to Start, and How to Write a Book. He's sold more than 100,000 books, in twelve languages. His weekly \"Love Mondays\" newsletter offers writers two minutes of wisdom on how to make it as a creative.",
    backgroundImage: "/assets/creators/david-background.png",
    portrait: "/assets/creators/david.png",
    trayColor: "#F2ECEC",
    textColor: "#383838",
    transparentColor: "rgba(50,50,50,.15)",
    urls: [
      {
        title: "David's Website",
        href: "https://kadavy.net",
      },
    ],
    plugins: ["gyFviYav4", "vXr2UFz5Q", "vKVcrG3Jb"],
  },
  {
    name: "Michael La Ronn",
    firstName: "Michael",
    slug: "michael",
    description:
      "Michael La Ronn is the founder of Author Level Up, where he shares resources that help authors write world class stories. He has published many science fiction & fantasy books and self-help books for writers. He built a writing career publishing 10-12 books per year while raising a family, working a full-time job, and even attending law school classes in the evenings.",
    backgroundImage: "/assets/creators/michael-background.png",
    portrait: "/assets/creators/michael.png",
    trayColor: "rgb(89, 89, 89)",
    textColor: "#fff",
    transparentColor: "rgba(217,217,217,.2)",
    urls: [
      {
        title: "Michael's Website",
        href: "https://www.michaellaronn.com",
      },
      {
        title: "Author Level Up",
        href: "https://www.authorlevelup.com",
      },
    ],
    plugins: ["QlQAIWB0L", "FRXmKpBnd", "gq4lloYeg", "iosrPDbzI"],
  },
  {
    name: "Melle Amade",
    firstName: "Melle",
    slug: "melle",
    description:
      "Melle is a USA Today Bestselling paranormal fantasy romance author and an authority in using artificial intelligence into the creative process. She has written over 40 pre-AI romance novels and as she has co-authored 17 nonfiction books on using AI. Melle co-launched 'AI4 Authors' in March 2022, which teaches authors how to effectively use AI.",
    backgroundImage: "/assets/creators/melle-background.png",
    portrait: "/assets/creators/melle.png",
    trayColor: "#ECECF4",
    textColor: "#383838",
    transparentColor: "rgba(50,50,50,.2)",
    urls: [
      {
        title: "AI4 Authors",
        href: "https://ai4ces.mykajabi.com/",
      },
    ],
    plugins: ["uFYd9Barz", "vB6F7n2XD", "tPCWZg2hw"],
  },
];

export const MIXPANEL_PRODUCTION_TOKEN = "963898499bf48cc69b32590d149deabf";
export const MIXPANEL_DEV_TOKEN = "0e3eac5746f63d81f528d01c8440c9cd";

export const DEV_CONCIERGE_PAYMENT_LINK_ID = "plink_1RGmbaFmAW2gX81XB3y087My";
export const PROD_CONCIERGE_PAYMENT_LINK_ID = "plink_1RHu39FmAW2gX81XNa1R7qBt";
export const DEV_CONCIERGE_PRODUCT_ID = "prod_SB8rf96U65sQY8";
export const PROD_CONCIERGE_PRODUCT_ID = "prod_SCIeQEJU6wUwyU";
export const DEV_MAX_PLAN_PRODUCT_ID = "prod_OvIpm7Ie3aT6V7";
export const PROD_MAX_PLAN_PRODUCT_ID = "prod_P6vc3V3kEwRT9S";

// POV and Tense
export const POVS = {
  NONE: "none", // Keep for backward compatibility
  AUTO: "auto", // New Auto value
  THIRD_PERSON_LIMITED: "3rd person limited",
  THIRD_PERSON_OMNISCIENT: "3rd person omniscient",
  THIRD_PERSON_OBJECTIVE: "3rd person objective",
  FIRST_PERSON: "1st person",
  SECOND_PERSON: "2nd person",
  // Abbreviated versions for display
  NONE_ABBR: "POV: none", // Keep for backward compatibility
  AUTO_ABBR: "Choose POV", // New Auto abbreviation without POV prefix
  THIRD_PERSON_LIMITED_ABBR: "3rd person ltd. POV",
  THIRD_PERSON_OMNISCIENT_ABBR: "Omniscient POV",
  THIRD_PERSON_OBJECTIVE_ABBR: "Objective POV",
  FIRST_PERSON_ABBR: "1st person POV",
  SECOND_PERSON_ABBR: "2nd person POV",
};

export const TENSES = {
  NONE: "none", // Keep for backward compatibility
  AUTO: "auto", // New Auto value
  PAST: "past",
  PRESENT: "present",
  FUTURE: "future",
};

export const GET_POV_ABBREVATION = (pov) => {
  switch (pov) {
    case POVS.NONE:
      return POVS.NONE_ABBR;
    case POVS.THIRD_PERSON_LIMITED:
      return POVS.THIRD_PERSON_LIMITED_ABBR;
    case POVS.THIRD_PERSON_OMNISCIENT:
      return POVS.THIRD_PERSON_OMNISCIENT_ABBR;
    case POVS.THIRD_PERSON_OBJECTIVE:
      return POVS.THIRD_PERSON_OBJECTIVE_ABBR;
    case POVS.FIRST_PERSON:
      return POVS.FIRST_PERSON_ABBR;
    case POVS.SECOND_PERSON:
      return POVS.SECOND_PERSON_ABBR;
    default:
      return pov;
  }
};

export const DEFAULT_POV_TENSE_OBJECT = { pov: { value: POVS.THIRD_PERSON_LIMITED, characters: [], userChanged: false }, tense: { value: TENSES.PAST, userChanged: false } };

export const POV_OPTIONS = [
  {
    value: POVS.THIRD_PERSON_LIMITED,
    label: `${POVS.THIRD_PERSON_LIMITED} POV (most common)`,
    description: "Stays close to only one character's thoughts.",
  },
  {
    value: POVS.THIRD_PERSON_OBJECTIVE,
    label: `${POVS.THIRD_PERSON_OBJECTIVE} POV`,
    description: "Writes what a camera would see. No thoughts, just action.",
  },
  {
    value: POVS.THIRD_PERSON_OMNISCIENT,
    label: `${POVS.THIRD_PERSON_OMNISCIENT} POV`,
    description: "Reveals every character’s thoughts.",
  },
  { value: POVS.FIRST_PERSON, label: `${POVS.FIRST_PERSON} POV`, description: "Tells the story through the narrator's eyes." },
  { value: POVS.SECOND_PERSON, label: `${POVS.SECOND_PERSON} POV`, description: 'Makes "you" the main character.' },
];

export const TENSE_OPTIONS = [
  { value: TENSES.PAST, label: "Past tense", description: "Creates a feeling of narrative story-telling, with a small distance between the reader and the characters." },
  { value: TENSES.PRESENT, label: "Present tense", description: "Creates a more action-packed, dynamic tone. Commonly paired with first-person POV." },
  { value: TENSES.FUTURE, label: "Future tense", description: "An unusual choice, useful when you want to create contrast, such as in a prophecy or fable." },
];

export const createPOVTenseObject = (pov, tense) => {
  return {
    pov: { ...DEFAULT_POV_TENSE_OBJECT.pov, ...(pov || {}) },
    tense: { ...DEFAULT_POV_TENSE_OBJECT.tense, ...(tense || {}) },
  };
};

/**
 * Common titles that should be preserved with the first name
 */
const COMMON_TITLES = new Set(["Mr", "Mrs", "Ms", "Miss", "Dr", "Prof", "Professor", "Sir", "Lady", "Lord", "Mr.", "Mrs.", "Ms.", "Dr.", "Prof.", "Sir.", "Lady.", "Lord."]);

/**
 * Extracts the first name or title + first name from a full name
 * Examples:
 * "Dr. Anne Smith" -> "Dr. Anne"
 * "Miss Anne Smith" -> "Miss Anne"
 * "John Smith" -> "John"
 * "Anne-Marie Smith" -> "Anne-Marie"
 * "Dr. John Smith Jr." -> "Dr. John"
 * "Mr. James Smith" -> "Mr. James"
 */
export const extractFirstName = (fullName) => {
  if (!fullName) return "";

  // Split by spaces
  const parts = fullName.split(" ");

  // If there's only one part, return it
  if (parts.length === 1) return parts[0];

  // Check if the first part is a common title
  if (COMMON_TITLES.has(parts[0])) {
    // Return first two parts (title + first name)
    return parts.slice(0, 2).join(" ");
  }

  // Otherwise return just the first part
  return parts[0];
};

// Helper functions for backward compatibility
export const normalizePOVValue = (value) => {
  // Convert "none" to "auto" for backward compatibility
  return value === POVS.NONE ? POVS.AUTO : value;
};

export const normalizeTenseValue = (value) => {
  // Convert "none" to "auto" for backward compatibility
  return value === TENSES.NONE ? TENSES.AUTO : value;
};

export const isAutoPOV = (value) => {
  return !value || value === POVS.NONE || value === POVS.AUTO;
};

export const isAutoTense = (value) => {
  return !value || value === TENSES.NONE || value === TENSES.AUTO;
};

export const UNLIMITED_EXPERIMENT_VARIATIONS = {
  CONTROL: "control",
  UNLIMITED_DEFAULT: "variation-unlimited-default",
  MUSE_DEFAULT: "variation-muse-default",
};

export const CHAPTER_EDITOR_QUALITY = {
  LOW: "low",
  HIGH: "high",
};

export const CHAPTER_EDITOR_MODELS = [
  { label: "Fast", value: "claude-sonnet-4-6" },
  { label: "Medium", value: "gemini-2.5-flash-preview-05-20" },
  { label: "High Quality", value: "gemini-2.5-pro" },
];

export const CHAPTER_EDITOR_DEFAULT_MODEL = "gemini-2.5-pro";

/**
 * SW-8768: Backup model used by the Feedback (/api/ai/editor) pipeline when the
 * primary model (Gemini 2.5) returns a retryable error (5xx, 429, timeout,
 * network failure, "overloaded"). The fallback is per-chunk: only the chunk
 * whose primary call failed is replayed on the backup. Set to null to disable
 * the fallback entirely (kill switch).
 */
export const CHAPTER_EDITOR_BACKUP_MODEL = "claude-sonnet-4-6";

export const CHAPTER_EDITOR_CONTENT_LIMIT = 4000;

export const CHAPTER_EDITOR_SUGGESTION_TYPES = {
  accuracy: {
    label: "Accuracy",
    description: "Check for contradictions with the Story Bible",
    goal: "flag moments that contradict established story bible information.",
    watchFor: [
      "Character details that don't match their story bible description",
      "Worldbuilding elements that don't match the story bible",
      "Plot elements that don't match the outline. The current chapter is marked as scene_summary (but this may not exist).",
      "Rules of the world that contradict established lore",
      "Any other details that may be an oversight by the author",
      "Character actions that don't match their description in the story bible",
    ],
    requirements: [
      "You will only check for inaccuracies, not missing information. The chapter may be incomplete, and that is OK.",
      "Do not comment on things that are accurate. Only comment on things that are inaccurate.",
    ],
    examples: [
      {
        comment: "Story bible states Sarah has blue eyes, but here they're described as brown",
        line: 1,
        original: "Sarah's brown eyes widened in surprise.",
      },
      {
        comment: "The story bible establishes magic can't be used indoors, but here it is",
        line: 2,
        original: "She cast the spell inside the temple.",
      },
    ],
  },
  cliches: {
    label: "Cliches",
    description: "Check for overused literary phrases and tropes",
    goal: "flag moments that use tired or overused expressions and writing patterns.",
    watchFor: [
      "Common literary cliches and overused phrases",
      "Predictable metaphors and similes",
      "Overused character reactions and gestures",
      "Tired scene transitions and descriptions",
      "Formulaic dialogue patterns",
    ],
    requirements: ["When you comment on a cliche, give one or two ways to rewrite it so that it's more original."],
    examples: [
      {
        comment: "This is an overused phrase - consider a more original way to describe held breath",
        line: 1,
        original: "She let out a breath she didn't know she'd been holding.",
      },
      {
        comment: "This is a very common scene opener - try something more unique",
        line: 2,
        original: "Sunlight streamed through the dusty windows.",
      },
      {
        comment: "Knuckles turning white is an overused way to show tension - consider a fresher detail",
        line: 3,
        original: "His knuckles turned white as he gripped the steering wheel.",
      },
      {
        comment: "The shiver down spine trope is quite common - try describing the fear more originally",
        line: 4,
        original: "A shiver ran down her spine as she entered the dark room.",
      },
    ],
  },
  genre: {
    label: "Genre",
    description: "Check for genre appropriate elements (specify a genre in your Story Bible for best results).",
    goal: "flag moments that don't match the genre",
    watchFor: [
      "Make sure everything is aligned with the genre expectations",
      "Language or dialogue that feels out of place for the genre",
      "Character archetypes that feel out of place for the genre",
      "Pacing that doesn't match genre expectations",
      "World-building elements that clash with the genre",
    ],
    examples: [
      {
        comment: "Romance novel suddenly shifts to horror with graphic violence",
        line: 1,
        original: "Blood sprayed across the walls as the demon ripped through his chest.",
      },
      {
        comment: "Casual modern slang feels out of place in this medieval fantasy setting",
        line: 2,
        original: "That's totally awesome, my dude! High five!",
      },
    ],
  },
  boring: {
    label: "Pacing",
    description: "Identify slow or unnecessary sections",
    goal: "flag moments that stall momentum or add little artistic value.",
    watchFor: [
      "Info-dumps (world-building, backstory, tech specs)",
      "Mundane routine or scene-setting that doesn't reveal character or escalate conflict",
      "Redundant beats (emotions, descriptions, explained twice)",
      'Transitions with no tension ("They walked to the car.")',
      "Overly abstract reflection disconnected from scene",
    ],
    examples: [
      {
        comment: "Lengthy catalog of decor slows the scene; keep only what matters to mood or plot",
        line: 1,
        original: "The room was twelve feet by fifteen, with beige walls, a speckled gray carpet, and an acoustic-tile ceiling.",
      },
      {
        comment: "Routine action that doesn't advance story -- consider skipping to the inciting event",
        line: 2,
        original: "He poured cereal, added milk, and ate in silence.",
      },
    ],
  },
  confusing: {
    label: "Clarity",
    description: "Find unclear or ambiguous passages",
    goal: "flag passages where the reader may stumble or misinterpret.",
    watchFor: [
      'Pronoun ambiguity ("She grabbed her bag." Who is she?)',
      "Time/space jumps without grounding cues",
      'Unclear causal logic ("Because of that, John left." Because of what?)',
      "Rules of the world, magic, or tech left vague when they matter to the payoff",
    ],
    examples: [
      {
        comment: "Earlier we saw the car explode -- how is it drivable now?",
        line: 1,
        original: "She turned the key; the engine roared again.",
      },
      {
        comment: "Multiple male characters present -- specify which 'he'",
        line: 2,
        original: '"I can\'t do this," Maya whispered. "Then don\'t," he said.',
      },
    ],
  },
  credibility: {
    label: "Believability",
    description: "Spot unrealistic or poorly justified events",
    goal: "flag moments that break verisimilitude -- character, social, or physical logic",
    watchFor: [
      "Motivation leaps (sudden betrayal, love, courage) without set-up",
      "Skill/knowledge leaps (rookie hacker cracks NSA in an hour)",
      "Convenient coincidences or deus ex machina",
      "Social or legal implausibility (e.g., police ignore obvious evidence)",
      "Physics or biological impossibilities in an otherwise realistic world, or that doesn't follow the rules set out in that universe",
    ],
    examples: [
      {
        comment: "Feat feels unearned; consider lengthening her training or lowering opponent's skill.",
        line: 1,
        original: "After one afternoon of sword practice, Lia defeated the kingdom's champion.",
      },
      {
        comment: "Unlikely given modern media -- explain suppression or adjust stakes.",
        line: 2,
        original: "The CEO confessed to the crime in front of cameras, but no one reported it.",
      },
    ],
  },
  ending: {
    label: "Ending",
    description: "Evaluate the chapter's conclusion",
    goal: "evaluate how well the ending functions as a conclusion to the chapter.",
    requirements: [
      "A strong chapter ending should ideally provide some sense of resolution for a micro-arc or scene, while simultaneously creating anticipation or intrigue for the next chapter. It might deepen a theme, reveal a new facet of a character, raise the stakes, or introduce a new complication.",
      "Do NOT bow tie everything neatly.",
      "Do NOT make it saccharine or obvious. Do not simply state big themes or messages like a fortune cookie.",
      "Avoid last-second info-dumps, new characters, or deus ex machina",
    ],
  },
  lineEdit: {
    label: "Line Edits",
    description: "Get specific suggestions for improving prose",
    goal: "suggest specific line-level improvements to enhance clarity, flow, and impact.",
    watchFor: [
      "Awkward phrasing or word choice",
      "Inconsistent tense or POV",
      "Redundant words or phrases",
      "Weak verbs that could be stronger",
      "Overly complex sentence structures",
      "Opportunities for more vivid or precise language",
    ],
    requirements: [
      "Provide specific alternative wording for each suggestion",
      "Focus on improving the line while maintaining its original meaning",
      "Consider the surrounding context when making suggestions",
    ],
    examples: [
      {
        comment: "Consider using a stronger verb: 'stumbled' instead of 'walked unsteadily'",
        line: 1,
        original: "He walked unsteadily across the room.",
      },
      {
        comment: "Simplify the sentence structure for better flow",
        line: 2,
        original: "The fact that she was tired, which was something that happened often, made her irritable.",
      },
    ],
  },
};
